<template>
  <div class="game-view">
    <div class="content-container">
      <!-- Oberer Bereich: Modus & Status -->
      <div class="game-header">
        <div class="mode-display">
          <SpotifyStatusBadge />
        </div>
        
        <div v-if="currentTrackMeta" class="track-loaded-indicator">
          <span class="indicator-dot"></span>
          <span class="indicator-text">Karte geladen</span>
        </div>
      </div>

      <!-- Mittlerer Bereich: QR-Scanner oder Flip-Watcher -->
      <div class="game-content">
        <!-- Kein Track geladen -->
        <div v-if="!currentTrackUri" class="scan-prompt">
          <div class="scan-icon">📷</div>
          <h2>Bereit zum Scannen</h2>
          <p>Scanne den QR-Code auf deiner Spielkarte</p>
          <PrimaryButton
            size="lg"
            @click="openScanner"
          >
            Karte scannen
          </PrimaryButton>
        </div>

        <!-- Track geladen -->
        <div v-else class="track-ready">
          <div class="card-visual">
            <div class="card-icon">🎵</div>
            <p class="card-hint">Song bereit!</p>
          </div>

          <!-- Playback Controls -->
          <PlaybackControls
            :mode="mode"
            :is-playing="isPlaying"
            :can-play="canPlay"
            :has-user-pressed-play-button="hasUserPressedPlayButton"
            @play="handlePlay"
            @pause="handlePause"
          />
        </div>
      </div>

      <!-- Unterer Bereich: Aktionen -->
      <div class="game-actions">
        <PrimaryButton
          v-if="currentTrackUri"
          variant="secondary"
          size="md"
          full-width
          @click="scanNewCard"
        >
          Neue Karte scannen
        </PrimaryButton>

        <PrimaryButton
          variant="ghost"
          size="sm"
          full-width
          @click="goHome"
        >
          Zurück zum Hauptmenü
        </PrimaryButton>
      </div>

      <!-- Fehler-Anzeige -->
      <div v-if="error" class="error-banner">
        <p>⚠️ {{ error }}</p>
      </div>

      <!-- Loading Overlay -->
      <div v-if="isLoading" class="loading-overlay">
        <div class="loading-spinner"></div>
        <p>Lade Track...</p>
      </div>
    </div>

    <!-- QR Scanner Modal -->
    <QRScanner
      :is-open="scannerOpen"
      @scanned="handleScan"
      @close="closeScanner"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useGameState } from '@/composables/useGameState';
import SpotifyStatusBadge from '@/components/Spotify/SpotifyStatusBadge.vue';
import QRScanner from '@/components/Game/QRScanner.vue';
import PlaybackControls from '@/components/Game/PlaybackControls.vue';
import PrimaryButton from '@/components/UI/PrimaryButton.vue';

const router = useRouter();
const scannerOpen = ref(false);

const {
  mode,
  currentTrackUri,
  currentTrackMeta,
  isPlaying,
  hasUserPressedPlayButton,
  isLoading,
  error,
  canPlay,
  handleScan: handleGameScan,
  play,
  pause,
  reset
} = useGameState();

function openScanner() {
  scannerOpen.value = true;
}

function closeScanner() {
  scannerOpen.value = false;
}

async function handleScan(qrValue: string) {
  await handleGameScan(qrValue);

  // Nur Scanner schließen – Abspielen übernimmt der Play-Button
  scannerOpen.value = false;
}



function handlePlay() {
  play();
}

function handlePause() {
  pause();
}

function scanNewCard() {
  reset();
  openScanner();
}

function goHome() {
  reset();
  router.push('/');
}
</script>

<style scoped lang="scss">
.game-view {
  @apply min-h-screen flex items-center justify-center;
  @apply px-4 py-20;
  background: linear-gradient(135deg, $background-base 0%, $background-dark 100%);
}

.content-container {
  @apply w-full max-w-md;
  @apply flex flex-col gap-6;
  @apply relative;
}

.game-header {
  @apply flex items-center justify-between;
  @apply bg-gray-900 bg-opacity-50 rounded-xl p-4;
  @apply border border-gray-800;
  backdrop-filter: blur(10px);
}

.track-loaded-indicator {
  @apply flex items-center gap-2;
  @apply text-sm text-green-400;
  
  .indicator-dot {
    @apply w-2 h-2 rounded-full;
    @apply bg-green-400;
    background: $spotify-green;
    animation: pulse 2s infinite;
  }
}

.game-content {
  @apply bg-gray-900 bg-opacity-50 rounded-2xl p-8;
  @apply border border-gray-800;
  backdrop-filter: blur(10px);
  min-height: 400px;
  @apply flex items-center justify-center;
}

.scan-prompt {
  @apply text-center;
  @apply flex flex-col items-center gap-4;
  
  .scan-icon {
    @apply text-6xl mb-2;
    animation: scanPulse 2s infinite;
  }
  
  h2 {
    @apply text-2xl font-bold text-white m-0;
  }
  
  p {
    @apply text-gray-400 m-0;
  }
}

.track-ready {
  @apply w-full;
  @apply flex flex-col gap-6;
}

.card-visual {
  @apply text-center py-4;
  
  .card-icon {
    @apply text-5xl mb-2;
    animation: float 3s ease-in-out infinite;
  }
  
  .card-hint {
    @apply text-lg font-medium text-white m-0;
  }
}

.game-actions {
  @apply flex flex-col gap-3;
}

.error-banner {
  @apply bg-red-900 bg-opacity-30 text-red-400;
  @apply px-4 py-3 rounded-lg text-center;
  border: 1px solid rgba($error, 0.3);
  
  p {
    @apply m-0 text-sm;
  }
}

.loading-overlay {
  @apply absolute inset-0 rounded-2xl;
  @apply bg-black bg-opacity-80;
  @apply flex flex-col items-center justify-center gap-4;
  backdrop-filter: blur(5px);
  
  p {
    @apply text-white m-0;
  }
}

.loading-spinner {
  @apply w-12 h-12;
  @apply border-4 border-green-500 border-t-transparent;
  @apply rounded-full;
  border-color: $spotify-green transparent $spotify-green $spotify-green;
  animation: spin 1s linear infinite;
}

@keyframes scanPulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>